import type { ReactNode } from "react";
import { Ruler } from "lucide-react";

const Bar = ({ w = "w-full", h = "h-1.5" }: { w?: string; h?: string }) => (
  <div className={`${w} ${h} rounded-sm bg-white/[0.08]`} />
);

const Block = ({ className = "" }: { className?: string }) => (
  <div className={`rounded bg-white/[0.06] ${className}`} />
);

const AdBox = ({ label, className = "" }: { label: string; className?: string }) => (
  <div className={`rounded border border-dashed border-[#F59E0B] bg-[#F59E0B]/10 flex items-center justify-center ${className}`}>
    <span className="font-mono text-[8px] sm:text-[9px] font-bold text-[#FBBF24] tracking-wider">{label}</span>
  </div>
);

const MockNav = () => (
  <div className="flex items-center gap-1.5 mb-2">
    <div className="h-2 w-2 rounded-full bg-[#7C3AED]/70" />
    <Bar w="w-10" h="h-1" />
    <Bar w="w-6" h="h-1" />
    <Bar w="w-6" h="h-1" />
    <div className="ml-auto"><Bar w="w-8" h="h-2" /></div>
  </div>
);

const MOCKS: Record<string, ReactNode> = {
  homepage_banner: (
    <>
      <MockNav />
      <AdBox label="1440 × 200" className="h-7 w-full" />
      <div className="flex gap-2 mt-2 flex-1 min-h-0">
        <Block className="flex-1" />
        <div className="w-1/4 flex flex-col gap-2">
          <Block className="flex-1" />
          <Block className="flex-1" />
        </div>
      </div>
    </>
  ),
  in_feed: (
    <>
      <MockNav />
      <Block className="h-8 w-full" />
      <div className="grid grid-cols-3 gap-2 mt-2 flex-1 min-h-0">
        <Block />
        <AdBox label="1200 × 675" />
        <Block />
      </div>
    </>
  ),
  article_inline: (
    <>
      <MockNav />
      <Bar w="w-2/3" h="h-2.5" />
      <div className="space-y-1.5 mt-2">
        <Bar /><Bar w="w-11/12" />
      </div>
      <div className="flex items-center gap-2 my-2 rounded border border-dashed border-[#F59E0B] bg-[#F59E0B]/10 p-1.5">
        <AdBox label="16:9" className="h-7 w-12 shrink-0 border-solid" />
        <div className="flex-1 space-y-1">
          <Bar w="w-3/4" h="h-1.5" /><Bar w="w-1/2" h="h-1" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Bar /><Bar w="w-4/5" />
      </div>
    </>
  ),
  sidebar: (
    <>
      <MockNav />
      <div className="flex gap-2 flex-1 min-h-0">
        <div className="flex-1 flex flex-col gap-1.5">
          <Block className="h-10" />
          <Bar /><Bar w="w-5/6" /><Bar w="w-2/3" />
        </div>
        <div className="w-[34%] flex flex-col gap-2">
          <Block className="h-8" />
          <AdBox label="600 × 500" className="flex-1" />
        </div>
      </div>
    </>
  ),
  nav_menu: (
    <>
      <MockNav />
      <div className="rounded border border-white/10 bg-white/[0.02] p-2 flex-1 flex flex-col min-h-0">
        <div className="grid grid-cols-3 gap-2 flex-1 min-h-0">
          <Block /><Block /><Block />
        </div>
        <div className="flex items-center gap-2 mt-2 rounded border border-dashed border-[#F59E0B] bg-[#F59E0B]/10 p-1.5">
          <AdBox label="16:9" className="h-6 w-10 shrink-0 border-solid" />
          <div className="flex-1 space-y-1">
            <Bar w="w-2/3" h="h-1.5" /><Bar w="w-2/5" h="h-1" />
          </div>
        </div>
      </div>
    </>
  ),
  sector_inline: (
    <>
      <MockNav />
      <Bar w="w-1/3" h="h-2.5" />
      <div className="grid grid-cols-3 gap-2 mt-2 flex-1 min-h-0">
        <Block />
        <Block />
        <AdBox label="800 × 450" />
      </div>
    </>
  ),
  research_spotlight: (
    <>
      <MockNav />
      <Bar w="w-1/3" h="h-2.5" />
      <div className="grid grid-cols-3 gap-2 mt-2 flex-1 min-h-0">
        <Block className="col-span-2" />
        <AdBox label="920 × 560" />
      </div>
    </>
  ),
  premium_insight: (
    <>
      <MockNav />
      <Bar w="w-1/3" h="h-2.5" />
      <div className="grid grid-cols-3 gap-2 mt-2 flex-1 min-h-0">
        <Block className="col-span-2" />
        <div className="flex flex-col gap-2 min-h-0">
          <Block />
          <AdBox label="900 × 760" className="flex-1" />
        </div>
      </div>
    </>
  ),
  sector_mid: (
    <>
      <MockNav />
      <div className="grid grid-cols-3 gap-2 h-8">
        <Block /><Block /><Block />
      </div>
      <AdBox label="1200 × 200" className="h-6 w-full my-2" />
      <div className="grid grid-cols-2 gap-2 flex-1 min-h-0">
        <Block /><Block />
      </div>
    </>
  ),
  audio_brief: (
    <>
      <MockNav />
      <div className="flex-1 flex flex-col items-center justify-center gap-2.5">
        <div className="flex items-end gap-[3px] h-8" aria-hidden>
          {[4, 8, 13, 9, 15, 11, 16, 12, 7, 11, 14, 8, 5, 9, 12, 6].map((h, i) => (
            <div key={i} className="w-[3px] rounded-full bg-[#F59E0B]/70" style={{ height: `${h * 2}px` }} />
          ))}
        </div>
        <div className="rounded border border-dashed border-[#F59E0B] bg-[#F59E0B]/10 px-3 py-1">
          <span className="font-mono text-[8px] font-bold text-[#FBBF24] tracking-wider">
            "TODAY'S BRIEF IS BROUGHT TO YOU BY…"
          </span>
        </div>
      </div>
    </>
  ),
};

const SHEET = [
  { key: "homepage_banner", name: "Homepage Banner", ratio: "~7:1 ultra-wide",
    where: "Top of the homepage, above the breaking story — full content width, first thing every visitor sees. The frame caps at 180px tall on wide screens, so an ultra-wide ~7:1 creative displays edge-to-edge without cropping." },
  { key: "in_feed", name: "In-Feed Native", ratio: "16:9 landscape",
    where: "A sponsored story card inside the More Stories grid on the homepage, styled like the news around it." },
  { key: "research_spotlight", name: "Research Spotlight", ratio: "~16:10 landscape",
    where: "The premium card beside the Featured Analysis in the homepage Research & Deep Dives section — a ~460×300 on-screen unit with your creative on top and a sponsored-article footer. Supply 920×560 px for retina sharpness." },
  { key: "premium_insight", name: "Premium Insight", ratio: "~6:5 near-square (full-bleed, flexes portrait↔landscape)",
    where: "The cinematic intelligence card beneath Trending Now in the homepage More Stories section — your creative fills the card full-bleed at full brightness with ONLY a slim SPONSORED disclosure overlaid (no text is added over your artwork). Supply 900×760 px; the card flexes between ≈4:5 portrait and ≈6:5 landscape across screen sizes, so bake your headline/brand into the creative and keep it inside the central 70% safe area." },
  { key: "article_inline", name: "Article Inline", ratio: "16:9 landscape",
    where: "A compact native strip embedded mid-read inside the article body — your creative displays as a 16:9 thumbnail beside your headline. Maximum attention, zero banner blindness." },
  { key: "sidebar", name: "Sidebar Spotlight", ratio: "6:5 rectangle",
    where: "The article right rail, beneath the live market context and breaking coverage — your full creative is shown uncropped on every article read." },
  { key: "nav_menu", name: "Navigation Spotlight", ratio: "16:9 landscape",
    where: "A sponsored strip inside the Markets, Sectors and Latest mega menus — seen on every menu open, site-wide. Your creative displays as a 16:9 thumbnail beside your headline." },
  { key: "sector_inline", name: "Sector In-Feed", ratio: "16:9 landscape",
    where: "A sponsored story card inside the Latest feed on the Crypto, DeFi and Policy sector pages — natively styled, sector-adjacent context." },
  { key: "sector_mid", name: "Sector Mid Banner", ratio: "6:1 wide banner",
    where: "A wide display banner between modules on the Crypto, DeFi and Policy sector pages — full content width, always shown uncropped at your creative's natural ratio." },
  { key: "audio_brief", name: "Audio Brief Sponsor", ratio: "spoken sponsor line", noCreative: true,
    where: "One exclusive sponsor per day, mentioned by name at the top of the 60-second Daily Brief — on-site, Telegram and the podcast. No graphic needed: you write a 10–140 character sponsor line and we read it aloud." },
] as Array<{ key: string; name: string; ratio: string; where: string; noCreative?: boolean }>;

export const AdSpecSheet = ({ specs, active, onSelect }: {
  specs: any; active: string; onSelect: (key: string) => void;
}) => (
  <section className="mt-14" data-testid="ad-spec-sheet">
    <h2 className="flex items-center gap-2 font-heading font-bold text-white text-xl sm:text-2xl">
      <Ruler className="h-5 w-5 text-[#F59E0B]" /> Creative spec sheet
    </h2>
    <p className="text-gray-400 text-sm mt-2 max-w-2xl leading-relaxed">
      Exactly where each placement runs and the frame your graphic needs to fill.
      PNG / JPG / WebP up to 3MB — <span className="text-white font-semibold">animated GIF up to 5MB</span>.
    </p>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
      {SHEET.map((s) => {
        const spec = specs?.[s.key];
        return (
          <button key={s.key} type="button" onClick={() => onSelect(s.key)}
            data-testid={`spec-card-${s.key}`}
            className={`text-left bg-[#121212] border rounded-xl p-5 transition-colors ${
              active === s.key ? "border-[#F59E0B]/70" : "border-white/10 hover:border-[#F59E0B]/40"}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="font-heading font-bold text-white text-base">{s.name}</h3>
              <span className="font-mono text-[11px] font-bold text-[#FBBF24] bg-[#F59E0B]/10 border border-[#F59E0B]/40 rounded px-2 py-0.5 shrink-0">
                {s.noCreative ? "text only" : `${spec?.example || "—"} px`}
              </span>
            </div>
            <div className="h-36 flex flex-col bg-[#0B0B0B] border border-white/10 rounded-lg p-3 overflow-hidden">
              {MOCKS[s.key]}
            </div>
            <p className="text-xs text-gray-400 mt-3 leading-relaxed">{s.where}</p>
            <div className="flex flex-wrap gap-1.5 mt-3">
              <span className="font-mono text-[9px] uppercase tracking-wider text-gray-400 border border-white/15 rounded px-1.5 py-0.5">{s.ratio}</span>
              {s.noCreative ? (
                <>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-gray-400 border border-white/15 rounded px-1.5 py-0.5">10–140 chars</span>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-gray-400 border border-white/15 rounded px-1.5 py-0.5">no creative needed</span>
                </>
              ) : (
                <>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-gray-400 border border-white/15 rounded px-1.5 py-0.5">
                    min {spec?.min_width ?? "—"}px wide
                  </span>
                  <span className="font-mono text-[9px] uppercase tracking-wider text-gray-400 border border-white/15 rounded px-1.5 py-0.5">GIF OK</span>
                </>
              )}
            </div>
          </button>
        );
      })}
    </div>
  </section>
);
